/**
 * File Upload Configuration
 * 
 * This file contains configuration for file uploads including:
 * - Maximum file size limits
 * - Allowed file types for different upload types
 */

// Maximum file size in MB
export const MAX_UPLOAD_SIZE_MB = 3;

// Maximum file size in bytes
export const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

// Allowed file types for cover images
export const ALLOWED_COVER_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
];

// Allowed file types for profile pictures
export const ALLOWED_PROFILE_PICTURE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
];

// Allowed file types for registration documents (Word files and PDFs only)
export const ALLOWED_REGISTRATION_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
];

// Allowed file types for personal space documents (PDFs, Word files, and images)
export const ALLOWED_PERSONAL_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
];

// Max file size for personal space documents (matches backend — 5MB)
export const MAX_PERSONAL_DOCUMENT_SIZE_MB = 5;
export const MAX_PERSONAL_DOCUMENT_SIZE_BYTES = MAX_PERSONAL_DOCUMENT_SIZE_MB * 1024 * 1024;

// Max total storage for personal space (500MB)
export const MAX_TOTAL_PERSONAL_SPACE_MB = 500;
export const MAX_TOTAL_PERSONAL_SPACE_BYTES = MAX_TOTAL_PERSONAL_SPACE_MB * 1024 * 1024;

// Allowed file types for event supporting files (PDFs, Word files, and images)
export const ALLOWED_EVENT_SUPPORTING_FILES_TYPES = [
  'application/pdf',
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
];

// Helper function to format file size for display
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Helper function to validate file size
export const validateFileSize = (file, maxSizeBytes = MAX_UPLOAD_SIZE_BYTES) => {
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File size exceeds the maximum limit of ${MAX_UPLOAD_SIZE_MB}MB. Your file is ${formatFileSize(file.size)}.`,
    };
  }
  return { valid: true };
};

// Helper function to validate file type
export const validateFileType = (file, allowedTypes) => {
  if (!allowedTypes.includes(file.type)) {
    const allowedExtensions = allowedTypes.map(type => {
      if (type.includes('pdf')) return 'PDF';
      if (type.includes('msword')) return 'DOC';
      if (type.includes('wordprocessingml')) return 'DOCX';
      if (type.includes('jpeg') || type.includes('jpg')) return 'JPEG';
      if (type.includes('png')) return 'PNG';
      if (type.includes('webp')) return 'WebP';
      if (type.includes('gif')) return 'GIF';
      return type.split('/')[1]?.toUpperCase();
    }).filter(Boolean).join(', ');
    
    return {
      valid: false,
      error: `Invalid file type. Allowed types: ${allowedExtensions}`,
    };
  }
  return { valid: true };
};

